import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, UserCheck, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatDate } from '../lib/utils';

export default function IdentityReviewCard({ item, itemId, locale }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState(
    (item.candidates || [])[0]?.qbo_customer_id || ''
  );
  const isPending = item.status === 'pending';

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['manual-review'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const sameMutation = useMutation({
    mutationFn: async () =>
      (await api.post(
        `/manual-review/${encodeURIComponent(itemId)}/identity-same-person`,
        { qbo_customer_id: selected, notes }
      )).data,
    onSuccess: () => {
      toast.success(t('manual_review.identity_same_success'));
      invalidate();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || '';
      toast.error(`${t('manual_review.identity_resolve_error')}${detail ? `: ${detail}` : ''}`);
    },
  });

  const distinctMutation = useMutation({
    mutationFn: async () =>
      (await api.post(
        `/manual-review/${encodeURIComponent(itemId)}/identity-different`,
        { notes }
      )).data,
    onSuccess: () => {
      toast.success(t('manual_review.identity_distinct_success'));
      invalidate();
    },
    onError: () => toast.error(t('manual_review.identity_resolve_error')),
  });

  const candidates = item.candidates || [];

  return (
    <Card
      className="bg-card border-border border-l-4 border-l-[hsl(var(--accent))]"
      data-testid={`identity-review-item-${item.identity_rule}`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[hsl(var(--accent))]/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--accent))]">
            {t('manual_review.type_identity_review')} · {item.identity_rule}
          </span>
          {!isPending && (
            <span
              className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              data-testid={`identity-review-status-${itemId}`}
            >
              {t(`manual_review.status_${item.status}`) || item.status}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDate(item.created_at, locale)}
          </span>
        </div>

        <p className="text-[13px] font-medium" data-testid="identity-review-reason">
          {item.reason}
        </p>

        <div className="grid gap-2 lg:grid-cols-2">
          <div
            className="rounded-md border border-border bg-muted/60 p-2 text-[12px]"
            data-testid="identity-review-order-side"
          >
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('manual_review.identity_order_side')}
            </div>
            <div className="font-semibold">{item.order_full_name || '—'}</div>
            <div className="text-muted-foreground">
              {t('manual_review.identity_phone')}: {(item.order_phones || []).join(', ') || '—'}
            </div>
            <div className="text-muted-foreground">
              {t('manual_review.identity_user_id')}: <span className="font-mono">{item.tiktok_user_id || '—'}</span>
            </div>
            <div className="break-all text-muted-foreground">
              {t('manual_review.identity_address')}: {item.order_addr_key_v2 || '—'}
            </div>
            {item.created_qbo_customer_id && (
              <div className="mt-1 text-muted-foreground">
                {t('manual_review.identity_created_separate')}:{' '}
                <span className="font-mono font-semibold text-foreground">
                  {item.created_qbo_customer_id}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            {candidates.map((c) => (
              <label
                key={c.qbo_customer_id}
                className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 text-[12px] ${
                  selected === c.qbo_customer_id
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                    : 'border-border bg-muted/40'
                }`}
                data-testid={`identity-review-candidate-${c.qbo_customer_id}`}
              >
                <input
                  type="radio"
                  className="mt-1"
                  checked={selected === c.qbo_customer_id}
                  onChange={() => setSelected(c.qbo_customer_id)}
                  disabled={!isPending}
                  data-testid={`identity-review-radio-${c.qbo_customer_id}`}
                />
                <span className="min-w-0">
                  <span className="block font-semibold">
                    {c.display_name || '—'}{' '}
                    <span className="font-mono text-muted-foreground">#{c.qbo_customer_id}</span>
                  </span>
                  <span className="block text-muted-foreground">
                    {t('manual_review.identity_phone')}: {(c.phones_norm || []).join(', ') || '—'}
                  </span>
                  <span className="block break-all text-muted-foreground">
                    {t('manual_review.identity_address')}: {c.addr_key_v2 || '—'}
                  </span>
                  <span className="block text-muted-foreground">
                    {t('manual_review.identity_transactions')}: {c.transactions ?? 0}
                    {' · '}
                    {c.active
                      ? t('manual_review.identity_active')
                      : t('manual_review.identity_inactive')}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-md bg-[hsl(var(--muted))] p-2 text-[12px] text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span data-testid="identity-review-disclaimer">
            {t('manual_review.identity_disclaimer')}
          </span>
        </div>

        {isPending && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              className="flex-1"
              placeholder={t('common.notes')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="identity-review-notes"
            />
            <Button
              size="sm"
              disabled={sameMutation.isPending || !selected}
              onClick={() => sameMutation.mutate()}
              data-testid="identity-review-same-person"
            >
              {sameMutation.isPending
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <UserCheck className="h-4 w-4 mr-2" />}
              {t('manual_review.identity_same_button')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={distinctMutation.isPending}
              onClick={() => distinctMutation.mutate()}
              data-testid="identity-review-different-person"
            >
              {distinctMutation.isPending
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Users className="h-4 w-4 mr-2" />}
              {t('manual_review.identity_distinct_button')}
            </Button>
          </div>
        )}

        {!isPending && item.resolved_qbo_customer_id && (
          <div className="text-[12px] text-muted-foreground">
            {t('manual_review.identity_resolved_to')}:{' '}
            <span className="font-mono font-semibold text-foreground">
              {item.resolved_qbo_customer_id}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
